import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { LoginInfo } from "../services/api";
import apiService from "../services/api-service";
import { useTheme } from "./ThemeContext";
import { useDock } from "./DockContext";
import { useDisplayMode } from "./DisplayModeContext";
import { useLinkOrder } from "./LinkOrderContext";
import userManagementApi from "../services/user-management-api";
import { isProfileComplete } from "../utils/profileCompletion";

interface AuthContextType {
  user: LoginInfo["user"] | null;
  loading: boolean;
  error: string | null;
  remainingAttempts: number;
  isAccountLocked: boolean;
  lockTimeRemaining: number; // in seconds
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  isUsernameLocked: (username: string) => boolean; // Check if specific username is locked
  getUsernameLockTimeRemaining: (username: string) => number; // Get remaining lock time in seconds for a username
}

const AuthContext = createContext<AuthContextType | null>(null);

// In-memory store for client-side lock tracking (per username)
// This prevents rapid login attempts without relying on localStorage
const clientLockStore = new Map<
  string,
  { lockedUntil: number; lockDurationMs: number }
>();

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<LoginInfo["user"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [lockTimeRemaining, setLockTimeRemaining] = useState(0);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { initializeFromLoginInfo } = useTheme();
  const { initializeFromPreferences: initializeDockPosition } = useDock();
  const { initializeFromPreferences: initializeDisplayMode } = useDisplayMode();
  const { initializeFromPreferences: initializeLinkOrder } = useLinkOrder();

  const checkAuth = async () => {
    try {
      const info = await apiService.getLoginInfo();
      setUser(info.user || null);
      setError(null);

      // Initialize theme, language, and UI settings from LoginInfo preferences
      if (info.preferences) {
        // Initialize theme from API response
        if (info.preferences.themeMode) {
          initializeFromLoginInfo(info);
          localStorage.setItem("themeMode", info.preferences.themeMode);
        }

        // Initialize language from API response
        if (info.preferences.language) {
          i18n.changeLanguage(info.preferences.language);
          localStorage.setItem("language", info.preferences.language);
        }

        // Initialize UI settings (display mode and dock position) from settings field
        if (info.preferences.settings) {
          initializeDisplayMode(info.preferences.settings);
          initializeDockPosition(info.preferences.settings);
          initializeLinkOrder(info.preferences.settings);
        }
      } else {
        // Fallback to localStorage if preferences not available from API
        const savedThemeMode = localStorage.getItem("themeMode");
        if (savedThemeMode) {
          // Theme will be initialized by ThemeContext using localStorage
        }

        const savedLanguage = localStorage.getItem("language");
        if (savedLanguage) {
          i18n.changeLanguage(savedLanguage);
        }

        // Display mode and dock position will be initialized from localStorage by their contexts
      }
    } catch (err) {
      setUser(null);
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    try {
      // Check if user is locked on client-side
      const clientLock = clientLockStore.get(username);
      if (clientLock && clientLock.lockedUntil > Date.now()) {
        const remainingMs = clientLock.lockedUntil - Date.now();
        const remainingSeconds = Math.ceil(remainingMs / 1000);

        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

        const errorMessage = `Account is locked. Please try again in ${timeStr}.`;
        setError(errorMessage);
        setIsAccountLocked(true);
        setLockTimeRemaining(remainingSeconds);
        throw new Error(errorMessage);
      }

      setError(null);
      setRemainingAttempts(3);
      setIsAccountLocked(false);
      setLockTimeRemaining(0);

      await apiService.postLogin({ username, password });
      await checkAuth();

      // Check if user's profile is complete
      try {
        const userProfile = await userManagementApi.getMyProfile();
        if (!isProfileComplete(userProfile)) {
          // Redirect to profile page if profile is incomplete
          navigate("/profile");
        } else {
          // Redirect to home page if profile is complete
          navigate("/home");
        }
      } catch (profileErr) {
        // If we can't fetch profile, redirect to home page as fallback
        console.error("Failed to check profile completion:", profileErr);
        navigate("/home");
      }
    } catch (err) {
      let errorMessage =
        "Login failed. Please check your username and password and try again.";
      let remaining = 3;
      let locked = false;
      let lockDurationMs = 0;

      if (axios.isAxiosError(err) && err.response?.status === 401) {
        const data = err.response.data as any;

        // Use the error message from the backend if available
        if (data?.error) {
          errorMessage = data.error;
        }

        // Extract remaining attempts and lock status
        if (typeof data?.remainingAttempts === "number") {
          remaining = data.remainingAttempts;
        }
        if (typeof data?.isLocked === "boolean") {
          locked = data.isLocked;
        }

        // If account is locked on backend, store it on client-side
        if (locked) {
          // Extract lock duration from error message
          // Error message format: "Account is locked due to too many failed login attempts. Please try again in 959 minutes."
          let lockDurationMinutes = 60; // default 1 hour

          const minutesMatch = errorMessage.match(
            /Please try again in (\d+) minutes/,
          );
          if (minutesMatch && minutesMatch[1]) {
            lockDurationMinutes = parseInt(minutesMatch[1], 10);
          }

          lockDurationMs = lockDurationMinutes * 60 * 1000;
          const lockedUntil = Date.now() + lockDurationMs;
          clientLockStore.set(username, { lockedUntil, lockDurationMs });
          setLockTimeRemaining(Math.ceil(lockDurationMs / 1000));
        }
      }

      setError(errorMessage);
      setRemainingAttempts(remaining);
      setIsAccountLocked(locked);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await apiService.getLogout();
      setUser(null);
      navigate("/login");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Logout failed");
    }
  };

  // Check if a specific username is locked
  const isUsernameLocked = (username: string): boolean => {
    const clientLock = clientLockStore.get(username);
    return clientLock ? clientLock.lockedUntil > Date.now() : false;
  };

  // Get remaining lock time in seconds for a specific username
  const getUsernameLockTimeRemaining = (username: string): number => {
    const clientLock = clientLockStore.get(username);
    if (!clientLock) return 0;

    const remainingMs = clientLock.lockedUntil - Date.now();
    if (remainingMs <= 0) return 0;

    return Math.ceil(remainingMs / 1000);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        remainingAttempts,
        isAccountLocked,
        lockTimeRemaining,
        login,
        logout,
        checkAuth,
        isUsernameLocked,
        getUsernameLockTimeRemaining,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
